import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CATEGORIES } from "@oshimap/config";
import { categoryColors } from "@oshimap/design-tokens";
import type { MarkerViewModel } from "@oshimap/map-core";
import { clusterPoints } from "@oshimap/utils";
import type { LatLng } from "@oshimap/domain";
import type { WebBasemapAdapter } from "../../map/web-basemap";
import { TILE_SIZE, viewportBounds, toScreen, graticuleStep, scaleBar,
  basemapAbsence, basemapFallbackNote, emptyCapabilityRegistry } from "@oshimap/map-core";
import { play, haptic } from "../../lib/motion";
import { useMapGestures, type Camera } from "./use-map-gestures";
import "./map.css";
import { CategoryIcon } from "../../components/ui";

interface Props {
  adapter: WebBasemapAdapter;
  markers: MarkerViewModel[];
  selectedKey: string | null;
  userLocation: LatLng | null;
  onSelect: (m: MarkerViewModel) => void;
  /** 클러스터를 눌렀을 때. 첫 멤버를 고르는 게 아니라 묶인 것을 풀어야 한다. */
  onClusterOpen: (node: {
    latitude: number; longitude: number;
    members: readonly { latitude: number; longitude: number }[];
    /** 아무리 확대해도 안 갈라질 때 대신 고를 마커. */
    firstKey: string | null;
  }) => void;
  /** 카메라는 화면이 상태로 들고 있다. 여기서는 받아서 그리기만 한다. */
  camera: Camera;
  /** 사용자가 지도를 움직였을 때. */
  onCamera: (next: Camera) => void;
  /** 컨테이너 크기가 정해졌을 때. 사용자가 움직인 게 아니므로 바로 다시 조회한다. */
  onResized: (size: { width: number; height: number }) => void;
}

export function MapCanvas({ adapter, markers, selectedKey, userLocation, onSelect, onClusterOpen, camera, onCamera, onResized }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  /* 카메라가 바뀌면 마커 자리를 다시 계산해야 한다. */
  const resized = useRef(onResized);
  resized.current = onResized;
  /* 실제 SDK 는 제스처를 스스로 처리한다. 그때 우리 훅이 같이 끌면 지도가 두 번 움직인다. */
  useMapGestures(ref, camera, onCamera, !adapter.ownsGestures);
  /* SDK 의 투영이 바뀌면(draw) 핀 자리를 다시 계산한다. Mock 은 카메라 prop 이 바뀌면 어차피 다시 그린다. */
  const [, setTick] = useState(0);
  useEffect(() => adapter.onViewChanged(() => setTick((t) => t + 1)), [adapter]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    /* 크기를 어댑터에도 알린다 — 보이는 영역 계산이 화면과 어긋나면 안 된다. */
    const ro = new ResizeObserver(() => {
      const next = { width: el.clientWidth, height: el.clientHeight };
      adapter.setViewportSize(next);
      setSize(next);
      /* 크기가 바뀌면 보이는 영역도 바뀐다 — 사용자가 움직인 게 아니므로 바로 조회한다. */
      resized.current(next);
    });
    ro.observe(el);
    const first = { width: el.clientWidth, height: el.clientHeight };
    adapter.setViewportSize(first);
    setSize(first);
    resized.current(first);
    return () => ro.disconnect();
  }, [adapter]);

  /*
   * 클러스터 반경은 픽셀 단위인데 clusterPoints 는 도(degree) 로 계산한다.
   * 화면 폭을 0.05도로 가정하던 상수를 쓰면 줌을 바꿔도 묶이는 정도가 그대로였다 —
   * 실제 축척으로 환산한다.
   */
  /* clusterPoints 는 `id` 로 묶는다. 마커의 진짜 id 를 덮어쓰면 안 되므로
     클러스터링용 키는 따로 넘기고, 선택 시 원본 마커로 되돌린다. */
  const byKey = useMemo(() => new Map(markers.map((m) => [m.key, m])), [markers]);
  const nodes = useMemo(() => {
    if (size.width === 0) return [];
    return clusterPoints(markers.map((m) => ({ id: m.key, latitude: m.latitude, longitude: m.longitude })), {
      radiusPx: 52,
      pixelsPerDegree: pixelsPerDegreeAt(adapter),
      minPoints: 3,
    });
  }, [markers, size.width]);
  const resolve = (key: string): MarkerViewModel | undefined => byKey.get(key);

  /*
   * 핀 층. 실제 SDK 가 있으면 SDK 의 overlay pane 에 **포털**로 들어간다 — 그 pane 은 지도를 끌 때
   * 함께 움직이고 클릭을 받는다. Mock 이면 이 컴포넌트의 DOM 에 그대로 그린다.
   */
  const pane = adapter.overlayElement();
  const pins = (
    <>
      {userLocation ? (() => {
        const p = adapter.project(userLocation, size);
        return <span className="om-map__user" style={{ left: p.x, top: p.y }} aria-hidden="true" />;
      })() : null}

      {nodes.map((node) => {
        if (node.kind === "CLUSTER") {
          const p = adapter.project({ latitude: node.latitude, longitude: node.longitude }, size);
          const d = Math.min(56, 34 + node.count * 1.6);
          return (
            <button key={node.id} className="om-cluster" style={{ left: p.x, top: p.y, width: d, height: d }}
              onClick={(e) => {
                play(e.currentTarget, "mo-pop");
                onClusterOpen({
                  latitude: node.latitude, longitude: node.longitude,
                  members: node.members.map((mm) => {
                    const src = byKey.get(mm.id);
                    return { latitude: src?.latitude ?? node.latitude, longitude: src?.longitude ?? node.longitude };
                  }),
                  firstKey: node.members[0]?.id ?? null,
                });
              }}
              aria-label={`이 구역 ${node.count}곳 — 눌러서 펼치기`}>
              <span className="num">{node.count}</span>
            </button>
          );
        }
        const m = resolve(node.item.id);
        if (!m) return null;
        return <Pin key={m.key} m={m} adapter={adapter} size={size} selected={m.key === selectedKey} onSelect={onSelect} />;
      })}
    </>
  );

  return (
    <div
      className={`om-map${adapter.ownsGestures ? " om-map--sdk" : ""}`}
      ref={ref}
      /* 키보드로도 움직일 수 있어야 한다. 드래그·핀치는 키보드로 흉내낼 수 없다. SDK 는 자기 단축키가 있다. */
      tabIndex={adapter.ownsGestures ? -1 : 0}
      role={adapter.ownsGestures ? undefined : "application"}
      aria-label={adapter.ownsGestures ? undefined : "지도. 방향키로 이동, + - 로 확대·축소. 같은 장소를 아래 목록에서도 볼 수 있습니다."}
    >
      {adapter.ownsGestures ? null : (
        <>
          {/*
            * 바닥 지도가 없는 개발·시험 빌드에서만. 가짜 도로를 그리지 않고 **진짜인 것만** 그린다:
            * 위경도 격자와 거리 눈금자. 둘 다 실제 투영에서 계산하고 카메라를 따라 움직인다.
            */}
          <Graticule camera={camera} size={size} />
          <p className="sr-only">
            {/* 이유는 map-core 한 곳에서 만든다. 결정이 바뀌면 두 플랫폼이 같이 바뀐다. */}
            {basemapFallbackNote(basemapAbsence(emptyCapabilityRegistry(), { point: camera.center }) ?? "NOT_CONFIGURED")}
            {" "}같은 장소를 아래 목록에서도 순서대로 확인할 수 있습니다.
          </p>
        </>
      )}
      {pane ? createPortal(pins, pane) : pins}
    </div>
  );
}

function Pin({ m, adapter, size, selected, onSelect }: {
  m: MarkerViewModel; adapter: WebBasemapAdapter; size: { width: number; height: number };
  selected: boolean; onSelect: (m: MarkerViewModel) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const p = adapter.project({ latitude: m.latitude, longitude: m.longitude }, size);
  const def = CATEGORIES[m.category];
  const color = selected ? "var(--map-marker-selected)" : categoryColors[m.category].fg;
  return (
    <button
      ref={ref}
      className={`om-pin${selected ? " is-selected" : ""}${m.dimmed ? " is-dimmed" : ""}`}
      style={{ left: p.x, top: p.y }}
      aria-pressed={selected}
      aria-label={`${m.name} · ${def.label}${m.isFavorited ? " · 저장됨" : ""}`}
      onClick={() => { play(ref.current, "mo-bounce"); haptic("BOUNCE"); onSelect(m); }}
    >
      <span className="om-pin__head" style={{ background: color }}>
        <span className="om-pin__glyph"><CategoryIcon code={m.category} size={19} /></span>
      </span>
      {m.activeEventCount > 0 ? <span className="om-pin__count num">{m.activeEventCount}</span> : null}
      {m.isFavorited ? <span className="om-pin__fav" aria-hidden="true" /> : null}
    </button>
  );
}

/** 현재 줌에서 경도 1도가 몇 픽셀인가. Web Mercator 는 가로 축척이 위도와 무관하다. */
function pixelsPerDegreeAt(adapter: WebBasemapAdapter): number {
  const cam = adapter.getCamera();
  const zoom = cam?.zoom ?? 14;
  return (TILE_SIZE * Math.pow(2, zoom)) / 360;
}

/**
 * 위경도 격자 + 거리 눈금자.
 *
 * 바닥 지도(타일)는 Google Basemap SDK를 연결하기 전까지 없습니다. 그 자리를 그림으로 메우지 않고
 * **실제 좌표에서 계산한 것만** 그린다 — 격자선은 진짜 경위선이고 눈금자는 진짜 거리다.
 * 그래서 확대·이동에 정확히 반응하고, 사용자가 "지금 얼마나 넓게 보고 있는지" 를 알 수 있다.
 */
function Graticule({ camera, size }: { camera: Camera; size: { width: number; height: number } }) {
  if (size.width === 0 || size.height === 0) return null;
  const b = viewportBounds(camera.center, camera.zoom, size);
  const step = graticuleStep(b.east - b.west);
  const bar = scaleBar(camera.center, camera.zoom, Math.min(96, size.width * 0.35));

  const verticals: number[] = [];
  for (let lng = Math.ceil(b.west / step) * step; lng < b.east; lng += step) {
    verticals.push(toScreen({ latitude: camera.center.latitude, longitude: lng }, camera.center, camera.zoom, size).x);
  }
  const horizontals: number[] = [];
  for (let lat = Math.ceil(b.south / step) * step; lat < b.north; lat += step) {
    horizontals.push(toScreen({ latitude: lat, longitude: camera.center.longitude }, camera.center, camera.zoom, size).y);
  }

  return (
    <>
      <svg className="om-map__grid" width={size.width} height={size.height} aria-hidden="true">
        {verticals.map((x, i) => <line key={`v${i}`} x1={x} y1={0} x2={x} y2={size.height} />)}
        {horizontals.map((y, i) => <line key={`h${i}`} x1={0} y1={y} x2={size.width} y2={y} />)}
      </svg>
      <div className="om-map__scale" aria-hidden="true">
        <span className="om-map__scalebar" style={{ width: `${Math.round(bar.px)}px` }} />
        <b className="num">{bar.label}</b>
      </div>
    </>
  );
}
